import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import StbMarkdown from '../security-toolbox-ui/StbMarkdown';
import { useChatStream } from '../../hooks/useChatStream';
import { useLocalChatSessions, useFetchChatSessionMessages } from '../../hooks/ai-hooks';

type UiMsg = { role: 'user' | 'assistant'; content: string };

const CIRCUIT_ICON_SRC = '/assets/images/circuit-dark.svg';

const newSessionId = () =>
  typeof crypto !== 'undefined' && crypto.randomUUID
    ? crypto.randomUUID()
    : `s-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;

const SUGGESTIONS = [
  'How do I position Cisco XDR?',
  'What certifications should I pursue?',
  'Help me prepare a demo',
  'Summarize Talos intel',
];

const PortalChatbot: React.FC = () => {
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState('');
  const [uiMessages, setUiMessages] = useState<UiMsg[]>([]);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const { sendMessageStream } = useChatStream();
  const { pathname } = useLocation();
  const { upsertSession } = useLocalChatSessions();
  const { refetch: refetchHistory } = useFetchChatSessionMessages(sessionId, { enabled: false });

  const scrollToBottom = useCallback(() =>
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }), []);

  useEffect(() => {
    scrollToBottom();
  }, [uiMessages, scrollToBottom]);

  useEffect(() => {
    if (open) inputRef.current?.focus();
  }, [open]);

  const handleNewChat = useCallback(() => {
    setSessionId(null);
    setUiMessages([]);
    setInput('');
  }, []);

  const handleSend = useCallback(async (text?: string) => {
    const userText = (text || input).trim();
    if (!userText || sending) return;
    setInput('');
    setSending(true);

    let sid = sessionId;
    if (!sid) {
      sid = newSessionId();
      setSessionId(sid);
      upsertSession(sid, userText.slice(0, 40));
    }

    try {
      await sendMessageStream({
        sessionId: sid,
        userText,
        useDrift: true,
        setUiMessages,
        refetchHistory,
        // The surface the user is on when they ask, forwarded as grounding context.
        path: pathname,
      });
    } catch {
      setUiMessages(prev => {
        if (!prev.length) return prev;
        const next = [...prev];
        const last = next[next.length - 1];
        if (last.role === 'assistant' && !last.content) {
          next[next.length - 1] = {
            ...last,
            content: "Sorry, I couldn't reach the AI service right now. Please try again later.",
          };
        }
        return next;
      });
    } finally {
      setSending(false);
    }
  }, [input, sending, sessionId, sendMessageStream, upsertSession, refetchHistory, pathname]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  }, [handleSend]);

  return (
    <>
      {/* Floating Action Button */}
      <button
        className="v2__chat-fab"
        onClick={() => setOpen(o => !o)}
        aria-label={open ? 'Close chat' : 'Open AI assistant'}
        title="AI Assistant"
      >
        {open ? (
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
          </svg>
        ) : (
          <img
            src={CIRCUIT_ICON_SRC}
            alt="AI Assistant"
            className="v2__chat-fab-icon"
          />
        )}
      </button>

      {/* Chat Panel */}
      {open && (
        <div className="v2__chat-panel">
          {/* Curved gradient header */}
          <div className="v2__chat-header">
            <svg className="v2__chat-header-wave" viewBox="0 0 400 140" preserveAspectRatio="none">
              <defs>
                <linearGradient id="chatGradBorder" x1="0" y1="0" x2="1" y2="0">
                  <stop offset="0%" stopColor="#3070e7" />
                  <stop offset="30%" stopColor="#16bdeb" />
                  <stop offset="60%" stopColor="#ff007f" />
                  <stop offset="100%" stopColor="#ff9000" />
                </linearGradient>
              </defs>
              {/* Dark blue fill */}
              <path d="M0 0h400v100c0 0-80 40-200 40S0 100 0 100z" fill="#1a2332" />
              {/* Gradient border at the bottom edge */}
              <path d="M0 100c0 0 80 40 200 40s200-40 200-40" fill="none" stroke="url(#chatGradBorder)" strokeWidth="3" />
            </svg>
            <div className="v2__chat-header-content">
              <div className="v2__chat-header-left">
                <div className="v2__chat-avatar">
                  <img
                    src={CIRCUIT_ICON_SRC}
                    alt="Bot"
                    width="24"
                    height="24"
                  />
                </div>
                <h3 className="v2__chat-title">Chat with us!</h3>
              </div>
              <div className="v2__chat-header-actions">
                <button className="v2__chat-icon-btn" onClick={handleNewChat} title="New conversation">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                    <circle cx="5" cy="12" r="2" fill="currentColor"/>
                    <circle cx="12" cy="12" r="2" fill="currentColor"/>
                    <circle cx="19" cy="12" r="2" fill="currentColor"/>
                  </svg>
                </button>
                <button className="v2__chat-icon-btn" onClick={() => setOpen(false)} title="Minimize">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                    <path d="M7 10l5 5 5-5" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </button>
              </div>
            </div>
          </div>

          {/* Online status bar */}
          <div className="v2__chat-online">
            <span className="v2__chat-online-dot" />
            We&apos;re online!
          </div>

          {/* Messages area */}
          <div className="v2__chat-messages">
            {uiMessages.length === 0 && (
              <div className="v2__chat-welcome">
                <div className="v2__chat-welcome-avatar">
                  <img
                    src={CIRCUIT_ICON_SRC}
                    alt="Bot"
                    width="32"
                    height="32"
                  />
                </div>
                <div className="v2__chat-welcome-bubble">
                  <p>Hello! I&apos;m your Security AI Assistant. How can I help you today?</p>
                </div>
                <div className="v2__chat-suggestions">
                  {SUGGESTIONS.map(s => (
                    <button
                      key={s}
                      className="v2__chat-suggestion"
                      onClick={() => handleSend(s)}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {uiMessages.map((m, idx) => (
              <div key={idx} className={`v2__chat-msg v2__chat-msg--${m.role}`}>
                {m.role === 'assistant' && (
                  <div className="v2__chat-msg-avatar">
                    <img
                      src={CIRCUIT_ICON_SRC}
                      alt="Bot"
                      width="18"
                      height="18"
                    />
                  </div>
                )}
                <div className="v2__chat-msg-bubble">
                  {m.role === 'assistant' ? (
                    <StbMarkdown>{m.content || '...'}</StbMarkdown>
                  ) : (
                    <span>{m.content}</span>
                  )}
                </div>
                {m.role === 'user' && (
                  <div className="v2__chat-msg-avatar v2__chat-msg-avatar--user">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                      <path d="M12 2a4 4 0 014 4v1a4 4 0 01-8 0V6a4 4 0 014-4zm-6 16c0-3.31 2.69-6 6-6s6 2.69 6 6v1H6v-1z" fill="currentColor"/>
                    </svg>
                  </div>
                )}
              </div>
            ))}

            {sending && uiMessages.length > 0 && !uiMessages[uiMessages.length - 1]?.content && (
              <div className="v2__chat-typing">
                <span /><span /><span />
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          {/* Input bar */}
          <div className="v2__chat-input-bar">
            <input
              ref={inputRef}
              className="v2__chat-input"
              type="text"
              placeholder="Enter your message..."
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              disabled={sending}
            />
            <button
              className="v2__chat-send"
              onClick={() => handleSend()}
              disabled={!input.trim() || sending}
              title="Send"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z" fill="currentColor"/>
              </svg>
            </button>
          </div>

          {/* Footer */}
          <div className="v2__chat-footer">
            <span>Powered by <strong>Security Hub AI</strong></span>
          </div>
        </div>
      )}
    </>
  );
};

export default PortalChatbot;
